import type { PackingMediaDiagnostic } from '@admin-panel/observability';

let attemptSequence = 0;
let diagnosticSink: Promise<typeof import('@admin-panel/observability')> | undefined;
type Failure = { code?: string; statusCode?: number; response?: { status?: number } };
export const packingFailureCategory = (failure: unknown): PackingMediaDiagnostic['category'] => {
  const error = failure as Failure | undefined;
  if (error?.code === 'PACKING_UPLOAD_STALLED') return 'STALLED';
  if (['ETIMEDOUT', 'ECONNABORTED'].includes(error?.code ?? '')) return 'TIMEOUT';
  if (error?.code === 'ERR_NETWORK') return 'NETWORK';
  if (Number(error?.statusCode) >= 1000) return 'APPLICATION_REJECTION';
  const http = error?.response?.status;
  if (http === 408) return 'TIMEOUT';
  if (http && http >= 500) return 'SERVER_ERROR';
  return http ? 'HTTP_REJECTION' : 'OTHER';
};

/** Per-attempt scalars only: never retains a File, request, grant, raw failure or URL. */
export const packingUploadDiagnostics = (context: {
  packageId?: number;
  bytes?: number;
  mimeType?: string;
  uploadMethod?: string | null;
  deviceCategory?: string | null;
  signal?: AbortSignal;
}) => {
  const started = performance.now();
  let reported = false;
  const evidence: PackingMediaDiagnostic = {
    packageId: context.packageId ?? 0,
    attemptId: `${Date.now().toString(36)}-${(++attemptSequence).toString(36)}`,
    phase: 'PREPARATION',
    category: 'OTHER',
    elapsedMs: 0,
    bytes: context.bytes,
    mimeType: context.mimeType,
    uploadMethod: context.uploadMethod ?? undefined,
    deviceCategory: context.deviceCategory ?? undefined,
  };
  return {
    phase(value: PackingMediaDiagnostic['phase']) {
      evidence.phase = value;
    },
    checkpoint(path: string) {
      evidence.uploadReference =
        path.match(/(?:^|\/)([0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12})(?:\/|$)/i)?.[1] ??
        (/^[0-9a-f]{32}$/i.test(path) ? path : undefined);
    },
    progress(loaded: number) {
      evidence.loaded = Math.round(loaded);
    },
    verification(value: PackingMediaDiagnostic['verification']) {
      evidence.verification = value;
    },
    report(
      failure: unknown,
      extra: Partial<Pick<PackingMediaDiagnostic, 'assetIds' | 'category'>> = {},
    ) {
      try {
        if (reported || context.signal?.aborted || !(evidence.packageId > 0)) return;
        reported = true;
        const error = failure as Failure | undefined;
        const category = packingFailureCategory(failure);
        const diagnostic = {
          ...evidence,
          ...extra,
          category:
            extra.category ??
            (category === 'OTHER' && ['INITIALIZATION', 'FINALIZATION'].includes(evidence.phase)
              ? 'UNCONFIRMED'
              : category),
          elapsedMs: Math.max(0, Math.round(performance.now() - started)),
          httpStatus: error?.response?.status,
          applicationCode: error?.statusCode,
        };
        diagnosticSink ??= import('@admin-panel/observability');
        void diagnosticSink
          .then(({ capturePackingMediaFailure }) => capturePackingMediaFailure(diagnostic))
          .catch(() => {
            // Diagnostic delivery must never affect an upload, retained checkpoint or attachment.
          });
      } catch {
        // Even malformed diagnostic input cannot replace the original business outcome.
      }
    },
  };
};
